<script setup lang="ts">
import DsAppHeader from '~/components/ds/components/layout/AppHeader.vue'

const config = useRuntimeConfig()
const authStore = useAuthStore()
const { isAdmin, canExportRnic } = useAuthorization()

/**
 * Lignes d'import en attente de revue (#137), administrateurs seulement ;
 * relu à chaque navigation, une validation faisant baisser le compteur.
 */
const route = useRoute()
const { data: draftCount, refresh: refreshDraftCount } = useAsyncData(
  'drafts-count',
  () => isAdmin.value
    ? $fetch<{ data: { pendingLines: number } }>(api.drafts.count()).catch(() => null)
    : Promise.resolve(null),
  { server: false },
)
watch(() => route.fullPath, () => { if (isAdmin.value) refreshDraftCount() })
const pendingLines = computed(() => draftCount.value?.data.pendingLines ?? 0)

/*
 * Traitements en arrière-plan en cours (imports, « Tout valider », « Annuler
 * l'import »), en temps réel : pastille sur l'entrée Import, seulement s'il
 * y en a. Le menu vient du design system (icônes seules) : la pastille est
 * posée dans le lien `/import` après le montage.
 */
const { runningCount } = useOperationsStream()
const mounted = ref(false)
onMounted(() => { mounted.value = true })

/** Entrées réservées à un niveau de rôle ; les autres sont ouvertes à tous. */
const navItems = computed(() => [
  { id: 'adherents', label: 'Adhérents', icon: 'i-lucide-users', to: '/members' },
  { id: 'adhesions', label: 'Adhésions', icon: 'i-lucide-file-text', to: '/subscriptions' },
  { id: 'coproprietes', label: 'Copropriétés', icon: 'i-lucide-building-2', to: '/condominiums' },
  { id: 'entites', label: 'Entités', icon: 'i-lucide-landmark', to: '/entities' },
  ...(canExportRnic.value
    ? [{ id: 'rnic', label: 'Registre RNIC', icon: 'i-lucide-scroll-text', to: '/rnic' }]
    : []),
  { id: 'renewals', label: 'Renouvellements', icon: 'i-lucide-clock', to: '/subscriptions/renewals' },
  { id: 'statistiques', label: 'Statistiques', icon: 'i-lucide-chart-column', to: '/stats' },
  ...(isAdmin.value
    ? [
        { id: 'import', label: runningCount.value ? `Import (${runningCount.value} traitement${runningCount.value > 1 ? 's' : ''} en cours)` : 'Import', icon: 'i-lucide-upload', to: '/import' },
        { id: 'export', label: 'Export', icon: 'i-lucide-download', to: '/export' },
        { id: 'drafts', label: pendingLines.value ? `Brouillons (${pendingLines.value})` : 'Brouillons', icon: 'i-lucide-clipboard-check', to: '/drafts' },
      ]
    : []),
])

const avatarUrl = computed(() => {
  const url = authStore.user?.avatar_url
  if (!url) return undefined
  return url.startsWith('/') ? `${config.public.ssoUrl}${url}` : url
})

const userInitial = computed(() => authStore.user?.first_name?.charAt(0)?.toUpperCase() || '?')

/** Entrée du menu utilisateur, au format attendu par `UDropdownMenu`. */
interface UserMenuItem {
  label: string
  icon: string
  to?: string
  color?: 'error'
  disabled?: boolean
  external?: boolean
  onSelect?: () => void
}

const userMenuItems = computed(() => {
  const items: UserMenuItem[][] = [
    [
      { label: authStore.user?.name || '', icon: 'i-lucide-user', disabled: true },
      { label: authStore.user?.email || '', icon: 'i-lucide-mail', disabled: true },
    ],
  ]
  if (isAdmin.value) {
    items.push([
      { label: 'Paramètres', icon: 'i-lucide-sliders-horizontal', to: '/settings' },
    ])
  }
  items.push([
    { label: 'Toutes les applications', icon: 'i-lucide-layout-grid', to: config.public.ssoUrl as string, external: true },
  ])
  items.push([
    { label: 'Déconnexion', icon: 'i-lucide-log-out', color: 'error', onSelect: () => authStore.logout() },
  ])
  return items
})
</script>

<template>
  <DsAppHeader
    icon="i-lucide-building-2"
    title="Adhérence"
    :nav-items="navItems"
    :user-menu-items="userMenuItems"
    :sso-apps-url="api.ssoApps()"
    :sso-url="config.public.ssoUrl as string"
    :avatar-url="avatarUrl"
    :user-name="authStore.user?.name"
    :user-initial="userInitial"
    show-dev-color-picker
  >
    <template v-if="isAdmin" #nav-extra>
      <span class="w-px h-5 bg-white/20 mx-1 shrink-0" />
      <UTooltip text="Paramètres">
        <UButton
          variant="ghost"
          color="neutral"
          icon="i-lucide-sliders-horizontal"
          class="text-white hover:bg-white/15 shrink-0"
          :class="$route.path.startsWith('/settings') ? 'bg-white/10' : ''"
          to="/settings"
        />
      </UTooltip>
    </template>
  </DsAppHeader>
  <Teleport v-if="mounted && isAdmin && runningCount" defer to="header a[href='/import']">
    <span class="nav-operations-dot" data-testid="nav-import-running">{{ runningCount }}</span>
  </Teleport>
</template>

<style>
/* Pastille du nombre de traitements en cours, dans le lien du menu Import. */
header a[href='/import'] {
  position: relative;
}
.nav-operations-dot {
  position: absolute;
  top: 1px;
  right: 1px;
  min-width: 1rem;
  height: 1rem;
  padding: 0 0.25rem;
  border-radius: 9999px;
  background: var(--ui-warning);
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1rem;
  text-align: center;
  pointer-events: none;
  animation: nav-operations-pulse 2s ease-in-out infinite;
}
@keyframes nav-operations-pulse {
  50% { opacity: 0.6; }
}
</style>
